import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Truck, Warning, Camera, Wrench, Drop, PaperPlaneTilt,
  ClockCounterClockwise, ShieldWarning,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const INCIDENT_OPTIONS = [
  { v: "", label: "Aucun incident" },
  { v: "accident", label: "Accident / choc" },
  { v: "panne", label: "Panne / voyant moteur" },
  { v: "pneu", label: "Pneu crevé / usé" },
  { v: "vitre", label: "Vitre / rétroviseur" },
  { v: "clim", label: "Climatisation HS" },
  { v: "autre", label: "Autre" },
];

const INCIDENT_LABEL = INCIDENT_OPTIONS.reduce((a, o) => { if (o.v) a[o.v] = o.label; return a; }, {});

export default function MyVehicle() {
  const [state, setState] = useState({ loading: true, vehicles: [], error: null });
  const [selectedId, setSelectedId] = useState(null);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({
    incident_kind: "",
    ct_needed: false, oil_needed: false, urgent: false,
    note: "", photo_path: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/vehicles/mine");
      const vs = r.data?.vehicles || [];
      setState({ loading: false, vehicles: vs, error: null });
      if (vs.length && !selectedId) setSelectedId(vs[0].id);
    } catch (err) {
      setState({ loading: false, vehicles: [], error: formatApiError(err) });
    }
  };

  useEffect(() => { load(); }, []);  // initial load

  const loadReports = async (vid) => {
    if (!vid) return;
    try {
      const r = await api.get(`/vehicles/${vid}/reports`);
      setReports(r.data || []);
    } catch { setReports([]); }
  };
  useEffect(() => { if (selectedId) loadReports(selectedId); }, [selectedId]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "file");
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, photo_path: r.data?.storage_path || r.data?.id || null }));
      toast.success("Photo prête à envoyer");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const payload = {
        incident_kind: form.incident_kind || null,
        ct_needed: form.ct_needed,
        oil_needed: form.oil_needed,
        urgent: form.urgent,
        note: form.note.trim() || null,
        photo_path: form.photo_path || null,
      };
      await api.post(`/vehicles/${selectedId}/report`, payload);
      toast.success("Remontée envoyée");
      setForm({ incident_kind: "", ct_needed: false, oil_needed: false, urgent: false, note: "", photo_path: null });
      loadReports(selectedId);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) return <div className="p-6 text-white/50 text-sm text-center">Chargement…</div>;

  if (state.error) {
    return (
      <div className="p-4">
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3 text-red-300">
          <Warning size={20} weight="duotone" /> <span className="text-sm">{state.error}</span>
        </div>
      </div>
    );
  }

  if (state.vehicles.length === 0) {
    return (
      <div className="p-4">
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-8 text-center space-y-3" data-testid="no-vehicle">
          <Truck size={40} weight="duotone" className="mx-auto text-white/30" />
          <div className="font-display font-semibold">Aucun véhicule attitré</div>
          <div className="text-sm text-white/60">Le gérant peut vous attitrer un véhicule depuis la Console → Outillage.</div>
        </div>
      </div>
    );
  }

  const vehicle = state.vehicles.find((v) => v.id === selectedId) || state.vehicles[0];
  const last = vehicle.last_report;

  return (
    <div className="p-4 space-y-4 pb-8 max-w-3xl mx-auto" data-testid="my-vehicle-page">
      {/* Vehicle picker (admin sees multiple) */}
      {state.vehicles.length > 1 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {state.vehicles.map((v) => (
            <button
              key={v.id}
              onClick={() => setSelectedId(v.id)}
              className={`shrink-0 px-3 py-2 rounded-full text-xs border ${selectedId === v.id ? "bg-[#005CFF] border-[#005CFF] text-white" : "bg-white/5 border-white/10 text-white/70"}`}
              data-testid={`vehicle-tab-${v.id}`}
            >
              {v.name} · {v.plate || "—"}
            </button>
          ))}
        </div>
      )}

      {/* Header vehicle card */}
      <div className="rounded-2xl bg-gradient-to-br from-[#005CFF]/15 to-white/[0.02] border border-white/10 p-4 flex items-center gap-3" data-testid="vehicle-card">
        <div className="w-14 h-14 rounded-xl bg-[#005CFF]/25 border border-[#005CFF]/40 flex items-center justify-center shrink-0">
          <Truck size={28} weight="duotone" className="text-[#7CB2FF]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg truncate">{vehicle.name}</div>
          <div className="text-xs text-white/60">
            {vehicle.brand || "—"} {vehicle.model || ""}
            {vehicle.plate && <span className="ml-2 font-mono bg-white/10 px-1.5 py-0.5 rounded">{vehicle.plate}</span>}
          </div>
          {last?.created_at && (
            <div className="text-[11px] text-white/40 mt-1">
              MàJ {new Date(last.created_at).toLocaleDateString("fr-FR")}
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3" data-testid="vehicle-report-form">
        <div className="flex items-center gap-2 font-display font-semibold text-sm">
          <PaperPlaneTilt size={16} weight="duotone" className="text-[#7CB2FF]" /> Faire remonter des infos
        </div>

        <FieldWrap icon={<ShieldWarning size={13} weight="bold" />} label="Incident">
          <select
            value={form.incident_kind}
            onChange={(e) => setForm((s) => ({ ...s, incident_kind: e.target.value }))}
            className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm"
            data-testid="vehicle-incident"
          >
            {INCIDENT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </FieldWrap>

        <div className="grid grid-cols-2 gap-2">
          <ToggleRow icon={<Wrench size={13} weight="bold" />} label="CT à programmer"
            checked={form.ct_needed} onChange={(v) => setForm((s) => ({ ...s, ct_needed: v }))}
            testid="vehicle-ct" />
          <ToggleRow icon={<Drop size={13} weight="bold" />} label="Vidange à programmer"
            checked={form.oil_needed} onChange={(v) => setForm((s) => ({ ...s, oil_needed: v }))}
            testid="vehicle-oil" />
        </div>

        <ToggleRow icon={<Warning size={13} weight="bold" className="text-red-300" />} label="⚠️ Signalement URGENT (notif direct admin)"
          checked={form.urgent} onChange={(v) => setForm((s) => ({ ...s, urgent: v }))}
          testid="vehicle-urgent" danger />

        <Textarea
          value={form.note}
          onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))}
          placeholder="Note libre (ex. crevaison arrière gauche, changement pneu à prévoir)"
          rows={3}
          className="bg-white/5 border-white/10"
          data-testid="vehicle-note"
        />

        <div className="flex items-center gap-2">
          <label className="flex-1 cursor-pointer inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-sm text-white/70">
            <Camera size={16} weight="duotone" className="text-[#7CB2FF]" />
            {uploading ? "Envoi…" : form.photo_path ? "Photo attachée ✓" : "Ajouter une photo"}
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="vehicle-photo-input" />
          </label>
          {form.photo_path && (
            <button onClick={() => setForm((s) => ({ ...s, photo_path: null }))}
              className="text-xs text-white/50 hover:text-red-300">Retirer</button>
          )}
        </div>

        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full h-11 bg-[#005CFF] hover:bg-[#0047CC]"
          data-testid="vehicle-submit"
        >
          {submitting ? "Envoi…" : "Envoyer la remontée"}
        </Button>
      </div>

      {/* History */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-2" data-testid="vehicle-history">
        <div className="flex items-center gap-2 font-display font-semibold text-sm">
          <ClockCounterClockwise size={16} weight="duotone" className="text-white/60" /> Historique
        </div>
        {reports.length === 0 ? (
          <div className="text-xs text-white/40 italic py-2">Aucune remontée pour l&apos;instant.</div>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => <HistoryRow key={r.id} r={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldWrap({ icon, label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 flex items-center gap-1">{icon} {label}</div>
      {children}
    </label>
  );
}

function ToggleRow({ icon, label, checked, onChange, testid, danger }) {
  return (
    <div className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${danger ? "bg-red-500/[0.06] border-red-500/20" : "bg-white/[0.02] border-white/5"}`}>
      <span className="text-xs text-white/80 flex items-center gap-1.5">{icon} {label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function HistoryRow({ r }) {
  const at = r.created_at ? new Date(r.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  const bits = [];
  if (r.incident_kind) bits.push(INCIDENT_LABEL[r.incident_kind] || r.incident_kind);
  if (r.ct_needed) bits.push("CT");
  if (r.oil_needed) bits.push("Vidange");
  const urgent = r.urgent || r.incident_kind;
  return (
    <div className={`rounded-lg border p-2.5 ${urgent ? "bg-red-500/[0.05] border-red-500/25" : "bg-white/[0.02] border-white/10"}`} data-testid={`vehicle-report-${r.id}`}>
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <div className="text-white/60">{r.reporter_name || "Tech"} · {at}</div>
        {urgent && <span className="text-[9px] font-bold uppercase tracking-widest text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded">Urgent</span>}
      </div>
      {bits.length > 0 && (
        <div className="mt-1 text-xs text-white/85 flex flex-wrap gap-x-2 gap-y-0.5">{bits.join(" · ")}</div>
      )}
      {r.note && <div className="mt-1 text-[12px] text-white/70 italic">« {r.note} »</div>}
      {r.photo_path && (
        <a href={`${API}/files/${r.photo_path}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[10px] text-[#7CB2FF] hover:underline">
          <Camera size={11} /> Voir la photo
        </a>
      )}
    </div>
  );
}
